/**
 * Payment method configuration builder
 * Handles the configuration logic for different Adyen payment methods
 */
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';
import { _t } from '@/locale';
import { adyenServices } from '@/services';
import { AvailablePaymentMethod, CheckoutEmployee, SelectedPaymentMethod } from '@/types/checkout';
import { KlarnaPaymentsProps } from '@adyen/adyen-web/dist/types/components/Klarna/types';
import { ADYEN_CONFIG } from '../config/adyen.config';
import { PaymentMethod, PaymentMethodType } from '../payments.services';
import { GooglePayProps, type KlarnaVariant, PaymentComponentConfig, UIElementProps } from '../types/adyen.types';

export const KLARNA_BRANDS: KlarnaVariant[] = ['klarna', 'klarna_account', 'klarna_paynow'];

const isBrandAboveMinimum = (
  brand: string,
  minimumAmounts: NonNullable<PaymentMethod['minimumAmounts']>,
  amount: number,
): boolean => {
  const rule = minimumAmounts.find((r) => r.id === brand);
  return !rule || amount >= rule.minimumAmount;
};

export const getValidKlarnaBrandsFromMethod = (klarnaMethod: PaymentMethod, amount?: number): KlarnaVariant[] =>
  (klarnaMethod.brands ?? []).filter(
    (b): b is KlarnaVariant =>
      (KLARNA_BRANDS as string[]).includes(b) &&
      (!klarnaMethod.minimumAmounts ||
        amount === undefined ||
        isBrandAboveMinimum(b, klarnaMethod.minimumAmounts, amount)),
  );

export const getValidKlarnaBrands = (paymentMethods: PaymentMethod[], amount?: number): KlarnaVariant[] => {
  const klarnaMethod = paymentMethods.find((pm) => pm.type === 'Klarna');
  if (!klarnaMethod) return [];
  return getValidKlarnaBrandsFromMethod(klarnaMethod, amount);
};

interface BuildConfigParams {
  paymentMethodType: SelectedPaymentMethod['type'];
  amount: number;
  googlePayLineItems?: google.payments.api.DisplayItem[];
  brands?: string[];
  klarnaVariant?: KlarnaVariant;
}

export const getPaymentMethodApiType = (type: SelectedPaymentMethod['type']): PaymentMethodType | null => {
  switch (type) {
    case CHECKOUT_PAYMENT_METHOD.NEW_COF:
    case CHECKOUT_PAYMENT_METHOD.STORED_COF:
      return 'Cards';
    case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
      return 'GooglePay';
    case CHECKOUT_PAYMENT_METHOD.KLARNA:
      return 'Klarna';
    case CHECKOUT_PAYMENT_METHOD.SWISH:
      return 'Swish';
    case CHECKOUT_PAYMENT_METHOD.WALLEY:
      return 'Walley';
    default:
      return null;
  }
};

/**
 * Builds the appropriate configuration for the selected payment method.
 * Returns null for unsupported payment methods instead of throwing,
 * allowing callers to handle the error gracefully.
 */
export const buildPaymentComponentConfig = ({
  paymentMethodType,
  amount,
  googlePayLineItems = [],
  brands,
  klarnaVariant,
}: BuildConfigParams): PaymentComponentConfig | null => {
  switch (paymentMethodType) {
    case CHECKOUT_PAYMENT_METHOD.NEW_COF:
      return {
        paymentComponent: 'card',
        manualSubmit: true,
        options: {
          // The surfaces that mount the card render their own submit button
          // (AddCardForm), and only that button flips the in-flight state.
          showPayButton: false,
          ...(brands && brands.length > 0 ? { brands } : {}),
        } as UIElementProps,
      };

    case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
      return {
        paymentComponent: 'googlepay',
        manualSubmit: true,
        options: {
          showPayButton: true,
          buttonType: 'pay',
          buttonSizeMode: 'fill',
          transactionInfo: {
            totalPrice: amount.toString(),
            totalPriceStatus: 'FINAL' as google.payments.api.TotalPriceStatus,
            totalPriceLabel: _t('total'),
            currencyCode: ADYEN_CONFIG.CURRENCY,
            countryCode: ADYEN_CONFIG.COUNTRY_CODE,
            displayItems: googlePayLineItems,
          },
        } as GooglePayProps,
      };

    case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
      return {
        paymentComponent: 'applepay',
        manualSubmit: true,
        options: {
          showPayButton: true,
          buttonType: 'check-out',
          countryCode: ADYEN_CONFIG.COUNTRY_CODE,
          onValidateMerchant: async (resolve: Function, reject: Function, validationUrl: string) => {
            try {
              const response = await adyenServices.validateMerchant(validationUrl, 'mp');
              resolve(response);
            } catch (error) {
              console.error('Apple Pay merchant validation failed:', error);
              reject(error);
            }
          },
        } as UIElementProps,
      };

    case CHECKOUT_PAYMENT_METHOD.KLARNA:
      return {
        paymentComponent: klarnaVariant ?? 'klarna',
        options: {
          showPayButton: false,
        } as KlarnaPaymentsProps,
      };

    case CHECKOUT_PAYMENT_METHOD.SWISH:
      return {
        paymentComponent: 'swish',
        options: {
          showPayButton: false,
        } as UIElementProps,
      };

    case CHECKOUT_PAYMENT_METHOD.WALLEY:
      return {
        paymentComponent: 'walley',
        options: {
          showPayButton: false,
        } as UIElementProps,
      };

    default:
      console.error(`Unsupported payment method type: ${paymentMethodType}`);
      return null;
  }
};

/**
 * Determines the modal title based on payment method and user state
 */
export const getModalTitle = (paymentMethodType?: SelectedPaymentMethod['type'], hasUser?: boolean): string => {
  switch (paymentMethodType) {
    case CHECKOUT_PAYMENT_METHOD.NEW_COF:
      return hasUser ? _t('addNewCard') : _t('completePayment');
    case CHECKOUT_PAYMENT_METHOD.KLARNA:
      return 'Klarna';
    case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
      return _t('continueToGoogle');
    case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
      return _t('continueToApple');
    case CHECKOUT_PAYMENT_METHOD.SWISH:
      return 'Swish';
    case CHECKOUT_PAYMENT_METHOD.WALLEY:
      return 'Walley';
    default:
      return _t('pay');
  }
};

/**
 * Gets the disclaimer label for add card form
 */
export const getDisclaimerLabel = (hasUser: boolean): string => {
  return hasUser ? _t('disclaimerSaveCard') : _t('disclaimerAcceptTerms');
};

export const getValidBokadirektPayPaymentMethods = ({
  paymentMethods,
  isSistaminuten = false,
  isDeal = false,
  employee = {},
  emailRequired = false,
  bookingTime,
  prepay = false,
  amount,
}: {
  paymentMethods: PaymentMethod[];
  isSistaminuten?: boolean;
  isDeal?: boolean;
  employee?: CheckoutEmployee['actual'];
  emailRequired?: boolean;
  bookingTime?: { startTimestamp?: number; dayTimestamp?: number };
  prepay?: boolean;
  amount?: number;
}): {
  hasKlarna: boolean;
  hasSwish: boolean;
  hasApplePay: boolean;
  hasGooglePay: boolean;
  hasStoredCoF: boolean;
  hasWalley: boolean;
} => {
  // TODO PAYNOW / ONLINEPAYMENTREQUIRED?

  const currentTime = new Date().getTime();

  if (bookingTime?.startTimestamp && bookingTime.startTimestamp < currentTime) {
    return {
      hasKlarna: false,
      hasSwish: false,
      hasApplePay: false,
      hasGooglePay: false,
      hasStoredCoF: false,
      hasWalley: false,
    };
  }

  const isValidTime = (method: PaymentMethod) => {
    if (!method.authorizationValidityInDays) {
      return true;
    }
    return (
      bookingTime?.dayTimestamp &&
      bookingTime.dayTimestamp < currentTime + method.authorizationValidityInDays * 24 * 3600 * 1000
    );
  };

  const checkIsValidSistaminutenPayment = () =>
    !Boolean(employee?.about?.settings?.onlinePaymentForSistaminutenOnly) || isSistaminuten || isDeal;

  const methodsByMpType = new Map(paymentMethods.map((method) => [method.mpType, method]));
  const klarnaMethod = methodsByMpType.get(CHECKOUT_PAYMENT_METHOD.KLARNA);
  const swishMethod = methodsByMpType.get(CHECKOUT_PAYMENT_METHOD.SWISH);
  const applePayMethod = methodsByMpType.get(CHECKOUT_PAYMENT_METHOD.APPLE_PAY);
  const googlePayMethod = methodsByMpType.get(CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY);
  const storedCoFMethod = methodsByMpType.get(CHECKOUT_PAYMENT_METHOD.STORED_COF);
  const walleyMethod = methodsByMpType.get(CHECKOUT_PAYMENT_METHOD.WALLEY);

  const isAboveMethodMinimum = (method: PaymentMethod): boolean => {
    if (!method.minimumAmounts || amount === undefined) return true;
    const rule = method.minimumAmounts.find((r) => r.id === method.type);
    return !rule || amount >= rule.minimumAmount;
  };

  const validKlarnaBrands = klarnaMethod ? getValidKlarnaBrandsFromMethod(klarnaMethod, amount) : [];

  return {
    hasKlarna: klarnaMethod
      ? isValidTime(klarnaMethod) && checkIsValidSistaminutenPayment() && validKlarnaBrands.length > 0
      : false,
    hasSwish: swishMethod
      ? isValidTime(swishMethod) &&
        checkIsValidSistaminutenPayment() &&
        (emailRequired || prepay) &&
        isAboveMethodMinimum(swishMethod)
      : false,
    hasApplePay: applePayMethod
      ? isValidTime(applePayMethod) &&
        checkIsValidSistaminutenPayment() &&
        (emailRequired || prepay) &&
        sessionStorage.getItem('canMakeApplePayment') === 'true' &&
        isAboveMethodMinimum(applePayMethod)
      : false,
    hasGooglePay: googlePayMethod
      ? isValidTime(googlePayMethod) &&
        checkIsValidSistaminutenPayment() &&
        (emailRequired || prepay) &&
        sessionStorage.getItem('canMakeGooglePayment') === 'true' &&
        isAboveMethodMinimum(googlePayMethod)
      : false,
    hasStoredCoF: storedCoFMethod
      ? isValidTime(storedCoFMethod) &&
        checkIsValidSistaminutenPayment() &&
        (emailRequired || prepay) &&
        isAboveMethodMinimum(storedCoFMethod)
      : false,
    hasWalley: walleyMethod
      ? isValidTime(walleyMethod) && checkIsValidSistaminutenPayment() && isAboveMethodMinimum(walleyMethod)
      : false,
  };
};

type ValidBokadirektPayMethods = ReturnType<typeof getValidBokadirektPayPaymentMethods>;

export const buildBokadirektPayOptions = (
  validPaymentMethods: ValidBokadirektPayMethods,
  { canPayOnline = true, hasPayAtPlace = false }: { canPayOnline?: boolean; hasPayAtPlace?: boolean } = {},
): AvailablePaymentMethod['type'][] => [
  ...(validPaymentMethods.hasApplePay && canPayOnline ? [CHECKOUT_PAYMENT_METHOD.APPLE_PAY] : []),
  ...(validPaymentMethods.hasGooglePay && canPayOnline ? [CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY] : []),
  ...(validPaymentMethods.hasStoredCoF && canPayOnline ? [CHECKOUT_PAYMENT_METHOD.STORED_COF] : []),
  ...(validPaymentMethods.hasSwish && canPayOnline ? [CHECKOUT_PAYMENT_METHOD.SWISH] : []),
  ...(hasPayAtPlace || !canPayOnline ? [CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE] : []),
  ...(validPaymentMethods.hasKlarna && canPayOnline ? [CHECKOUT_PAYMENT_METHOD.KLARNA] : []),
  ...(validPaymentMethods.hasWalley && canPayOnline ? [CHECKOUT_PAYMENT_METHOD.WALLEY] : []),
  ...(validPaymentMethods.hasStoredCoF && canPayOnline ? [CHECKOUT_PAYMENT_METHOD.NEW_COF] : []),
  CHECKOUT_PAYMENT_METHOD.NONE,
];
